// ─────────────────────────────────────────────────────────────────────────────
// La tira de filtros de Reportería, en el ORDEN de la maqueta:
//   buscar persona · OU · Fundamental · rango de fechas · limpiar
//
// 🔴 Por qué es propia y no la `FilterBar` compartida. `FilterBar` la usa también el módulo Analytics
// genérico (D-13) y su orden es otro (fechas → OU → Fundamental). Para llegar al orden de la maqueta
// había que reordenarla, y reordenar un componente compartido cambia una pantalla que nadie pidió
// tocar. Esta tira es de Reportería y solo de Reportería; `FilterBar` se queda intacta para Analytics.
//
// 🔴 BU / Site / Depto / Año / Mes se retiraron de esta tira (2026-09-02, pedido directo sobre la
// pantalla en producción). Año/Mes duplicaban el rango de fechas —que es lo que pide la Sección 7 de
// la Especificación: «el selector Año/Mes se reemplaza por un calendario de rango de fechas»— y BU/
// Site/Depto, sin catálogo maestro (RF-152), solo filtraban de verdad la pestaña Diario; en las otras
// seis el backend los ignoraba en silencio, justo el riesgo que ya advertía
// `docs/contexto/REPORTERIA-REGLAS.md` («un selector que no filtra nada es peor que ausente»).
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';
import { PersonPicker } from '@/components/common';
import { OuTreeSelect } from '@/components/common/OuTree';
import { useOuScope } from '@/hooks/useOuScope';
import type { ReportFilters } from './useRecognitionTabFilters';

export interface TiraDeFiltrosProps {
  readonly filters: ReportFilters;
  readonly setFilter: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void;
  readonly resetFilters: () => void;
  /** Fundamentales del filtro, con su nombre ya resuelto. */
  readonly fundamentales: readonly { id: string; nombre: string }[];
}

export function TiraDeFiltros({
  filters, setFilter, resetFilters, fundamentales,
}: TiraDeFiltrosProps) {
  const { t } = useTranslation('recognition');
  // Las OUs del alcance salen del MISMO hook que usa `FilterBar`: si esta tira armara su propia
  // lista, dos pantallas podrían ofrecer alcances distintos al mismo usuario.
  const { scopedOuItems } = useOuScope();

  return (
    <div className="rr-tira">
      {/* 1 · Buscar persona. Combobox contra el directorio: lo que se elige es una PERSONA (su id
             viaja al backend), no un texto que se compare con los nombres ya pintados. */}
      <PersonPicker
        value={filters.persona}
        onChange={(persona) => setFilter('persona', persona)}
        placeholder={t('reports.filters.personPlaceholder')}
      />

      {/* 2 · OU — el mismo árbol jerárquico que el resto de la app, ahora con selección múltiple
             (Fase 4 del plan de Reportería): cada OU elegida se expande a su subárbol en
             RecognitionAnalyticsView, no aquí — esta tira solo administra la SELECCIÓN. */}
      <div className="rr-tira-ou">
        <OuTreeSelect
          mode="multi"
          variant="dropdown"
          items={scopedOuItems}
          value={[...filters.ou]}
          onChange={(ids) => setFilter('ou', ids)}
          placeholder={t('reports.filters.allOus')}
        />
      </div>

      {/* 3 · Fundamental. */}
      <label className="rr-tira-campo">
        <span className="rr-sr-only">{t('reports.filters.fundamental')}</span>
        <select
          className="rr-funnel-select"
          value={filters.fundFilter}
          onChange={(e) => setFilter('fundFilter', e.target.value)}
          disabled={fundamentales.length === 0}
        >
          <option value="">{t('reports.filters.allFundamentals')}</option>
          {fundamentales.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
        </select>
      </label>

      {/* 4 · Rango de fechas. Por omisión, la semana en curso (`getCurrentWeekRange`). */}
      <div className="rr-tira-fechas">
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilter('dateFrom', e.target.value)}
          aria-label={t('reports.filters.dateFrom')}
        />
        <span aria-hidden="true">–</span>
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilter('dateTo', e.target.value)}
          aria-label={t('reports.filters.dateTo')}
        />
      </div>

      <button type="button" className="rr-btn rr-btn--ghost rr-tira-reset" onClick={resetFilters}>
        <i className="fas fa-rotate-left" aria-hidden="true" /> {t('reports.filters.reset')}
      </button>
    </div>
  );
}
